import { useCallback, useEffect, useMemo, useState } from 'react';
import { isNil } from 'es-toolkit';
import { FeatureFlag } from 'src/constants/feature-flag';
import { useFlagsAndLoadingState } from 'src/contexts/launch-darkly-flags-ready-context';
import { useActiveHouseholdId } from 'src/hooks/use-active-household-id';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import type { ClientPortalNavState } from './types';
import { GET_CLIENT_SIDEBAR_DATA } from '../../../../components/dashboard/client-sidebar-query';
import { CLIENT_PORTAL_NAV_BOOTSTRAP_TIMEOUT_MS, isClientPortalHideableSurface } from './constants';
import { clientPortalPortfolioOptions } from './utils';

export function useClientPortalNav(options?: { readonly enabled?: boolean }): ClientPortalNavState {
  const { user } = useAuth();
  const { activeHouseholdId, canQuery } = useActiveHouseholdId();
  const { flags } = useFlagsAndLoadingState();
  const enabled = options?.enabled !== false;

  const skipped = !enabled || isNil(user?.id) || !canQuery;
  const { data, loading, error, refetch } = useQueryWithErrorLogging(GET_CLIENT_SIDEBAR_DATA, {
    variables: { id: user?.id, householdId: activeHouseholdId ?? undefined },
    skip: skipped,
  });

  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false);
  const [waitAttempt, setWaitAttempt] = useState(0);

  useEffect(() => {
    if (!skipped) {
      setBootstrapTimedOut(false);
      return;
    }
    const timer = setTimeout(() => {
      setBootstrapTimedOut(true);
    }, CLIENT_PORTAL_NAV_BOOTSTRAP_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [skipped, waitAttempt]);

  const client = data?.client;
  const household = client?.household;
  const stalledBootstrap = enabled && skipped && bootstrapTimedOut;
  const failed = !isNil(error) || (!skipped && !loading && isNil(client)) || stalledBootstrap;

  const retry = useCallback(() => {
    setBootstrapTimedOut(false);
    setWaitAttempt((attempt) => attempt + 1);
    if (!skipped) {
      void refetch();
    }
  }, [refetch, skipped]);

  const personalPortfolioId = household?.portfolios.find((portfolio) => portfolio.isPersonal)?.id;
  const householdPortfolios = household?.portfolios;
  const portfolios = useMemo(() => clientPortalPortfolioOptions(householdPortfolios), [householdPortfolios]);

  // Fail open on error or while loading so a blip does not strip optional surfaces.
  const hiddenPortalSurfaces =
    failed || loading || skipped ? [] : (household?.hiddenPortalSurfaces ?? []).filter(isClientPortalHideableSurface);

  return {
    loading: enabled && !stalledBootstrap && (loading || skipped),
    failed,
    retry,
    personalPortfolioId: isNil(personalPortfolioId) ? null : String(personalPortfolioId),
    portfolios,
    enableTaxTab: client?.enableTaxTab === true,
    investmentsDisabled: household?.adviceOnly === true || isNil(household?.orionHouseholdId),
    hiddenPortalSurfaces,
    supportEnabled: flags[FeatureFlag.ClientPortalRedesignSupport] === true,
  };
}
